import React, { useState, useEffect, useCallback } from 'react';
import { Mail, RefreshCw, Loader } from 'lucide-react';
import api from '../utils/api';

const fmtDate = (iso) => new Date(iso).toLocaleString('en-CA', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

export const DigestRunHistory = () => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.get('/api/digest/runs');
      setData(res.data);
    } catch (e) {
      console.error('Digest runs load failed:', e);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="digest-run-history" className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Mail className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold">Digest Run History</h3>
            <p className="text-sm text-muted-foreground">Sent / skipped / failed per weekly digest batch</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="digest-runs-refresh-btn"
          className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {!data ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader className="h-5 w-5 animate-spin" />
        </div>
      ) : data.runs.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4" data-testid="digest-runs-empty">
          No digest runs yet. Runs appear here when the n8n schedule calls <code className="text-xs bg-background px-1 py-0.5 rounded">/api/digest/send-all</code>.
        </p>
      ) : (
        <div className="space-y-1" data-testid="digest-runs-list">
          <div className="grid grid-cols-12 gap-2 px-3 py-1 text-xs text-muted-foreground font-medium">
            <span className="col-span-4">Run Time</span>
            <span className="col-span-2">Eligible</span>
            <span className="col-span-2 text-center">Sent</span>
            <span className="col-span-2 text-center">Skipped</span>
            <span className="col-span-2 text-center">Failed</span>
          </div>
          {data.runs.map((run) => (
            <div
              key={run.run_id}
              data-testid={`digest-run-row-${run.run_id}`}
              className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-background rounded-lg text-sm"
            >
              <span className="col-span-4 text-xs">{fmtDate(run.run_at)}</span>
              <span className="col-span-2 text-xs text-muted-foreground capitalize">{run.total_eligible} ({run.day})</span>
              <span className="col-span-2 text-center text-xs text-green-400">{run.sent}</span>
              <span className="col-span-2 text-center text-xs text-muted-foreground">{run.skipped}</span>
              <span className={`col-span-2 text-center text-xs ${run.failed > 0 ? 'text-red-400 font-bold' : 'text-muted-foreground/50'}`}>
                {run.failed}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DigestRunHistory;
